import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { HelpCircle } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  useLeaveRequestQuery,
  useLeaveTypesQuery,
  useUpdateLeaveRequest,
} from "../api/queries";
import { HrTour } from "../tour/HrTour";
import { leaveRequestFormTourSteps } from "../tour/hrTourSteps";
import { useHrTour } from "../tour/useHrTour";
import type { LeaveRequestFormValues } from "../ui/LeaveRequestForm";
import { LeaveRequestForm } from "../ui/LeaveRequestForm";

export function LeaveRequestEditPage() {
  const { id } = useParams({ from: "/app/hr/leave/requests/$id/edit" });
  const navigate = useNavigate();
  const { data, isLoading } = useLeaveRequestQuery(id ?? "");
  const leaveTypes = useLeaveTypesQuery();
  const updateLeave = useUpdateLeaveRequest(id ?? "");
  const tour = useHrTour("hr-leave-form-tour-completed");

  const handleSubmit = (values: LeaveRequestFormValues) => {
    updateLeave.mutate(
      {
        leaveTypeId: values.leaveTypeId,
        startDate: values.startDate,
        endDate: values.endDate,
        reason: values.reason ?? undefined,
        attachmentKey: values.attachmentKey ?? undefined,
      },
      {
        onSuccess: () => {
          navigate({ to: "/app/hr/leave/requests/$id", params: { id: id! } });
        },
      },
    );
  };

  if (isLoading || !data) {
    return (
      <>
        <Header />
        <Main>
          <p className="text-muted-foreground">ກຳລັງໂຫລດ...</p>
        </Main>
      </>
    );
  }

  if (data.status !== "pending_line") {
    return (
      <>
        <Header />
        <Main>
          <p className="text-muted-foreground">
            ສາມາດແກ້ໄຂໄດ້ເມື່ອຍັງລໍຖ້າຫົວໜ້າສາຍ (ສະຖານະ ລໍຖ້າຫົວໜ້າສາຍ) ເທົ່ານັ້ນ.
          </p>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">ແກ້ໄຂຄຳຂໍລາ</h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                title="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            <p className="mt-1 text-muted-foreground">{data.leaveTypeName}</p>
          </div>
        </div>

        <LeaveRequestForm
          leaveTypes={leaveTypes.data ?? []}
          initialValues={{
            leaveTypeId: data.leaveTypeId,
            startDate: data.startDate.slice(0, 10),
            endDate: data.endDate.slice(0, 10),
            reason: data.reason ?? "",
            attachmentKey: data.attachmentKey ?? null,
          }}
          onSubmit={handleSubmit}
          submitting={updateLeave.isPending}
          submitLabel="ບັນທຶກການປ່ຽນແປງ"
          cancelTo="/app/hr/leave/requests"
          stickySubmit={false}
        />

        <HrTour
          steps={leaveRequestFormTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
